import { format, isSameDay } from "date-fns";
import React, { useCallback, useMemo } from "react";

import { faAngleDown, faCalendarDays } from "@performyard/fontawesome/pro-regular-svg-icons";
import { ButtonIcon, Icon, Text } from "@performyard/py-components";
import { ExtendedWorkspace, MeetingModelJson } from "@performyard/service-contracts";

import TruncatedText from "../../../../../components/Text/TruncatedText";
import { GoogleCalendarIcon } from "../../../../UserSettings/Views/Calendar/components/GoogleCalendarIcon";
import { OutlookCalendarIcon } from "../../../../UserSettings/Views/Calendar/components/OutlookCalendarIcon";
import MeetingAccordionContent from "./MeetingAccordionContent";
import { MeetingProvider, useMeetingContext } from "./MeetingContext";
import MeetingStatusBadge from "./MeetingStatusBadge";

const getFormattedMeetingDateString = (startDateTime: Date, endDateTime: Date) => {
	const startDate = format(startDateTime, "MMMM d, yyyy");
	const endDate = format(endDateTime, "MMMM d, yyyy");
	const startTime = format(startDateTime, "h:mmaaa");
	const endTime = format(endDateTime, "h:mmaaa");

	if (!isSameDay(startDateTime, endDateTime)) {
		return `${startDate} • ${startTime} - ${endDate} • ${endTime}`;
	} else {
		return `${startDate} • ${startTime} - ${endTime}`;
	}
};

const MeetingAccordionHeader = () => {
	const { meeting, isExpanded, toggleAccordion } = useMeetingContext();

	// Determine calendar integration display
	const renderCalendarIcon = () => {
		// Show generic calendar icon if meeting is not connected to calendar
		if (!meeting.calendarEvent?.eventId) {
			return <Icon size={20} icon={faCalendarDays} />;
		}

		// Show provider-specific icon based on the provider that was used to create the calendar event
		// This prevents showing the wrong icon when users switch providers after creating events
		const calendarProvider = meeting.calendarEvent?.provider;
		if (calendarProvider === "google") {
			return (
				<div className="flex h-5 w-5 items-center justify-center">
					<GoogleCalendarIcon />
				</div>
			);
		} else if (calendarProvider === "outlook") {
			return (
				<div className="flex h-5 w-5 items-center justify-center">
					<OutlookCalendarIcon />
				</div>
			);
		}

		// Fallback to generic calendar icon if provider is not recognized
		return <Icon size={20} icon={faCalendarDays} />;
	};

	return (
		<div
			className={`flex h-14 select-none items-center justify-between bg-neutral-1 px-4 hover:cursor-pointer hover:bg-neutral-2 ${
				isExpanded ? "rounded-t-lg" : "rounded-lg"
			}`}
			onClick={toggleAccordion}
			data-testid="meeting-accordion-header"
		>
			<div className="flex items-center space-x-4">
				{renderCalendarIcon()}
				<div className="flex flex-col items-start">
					<TruncatedText
						text={meeting.name}
						maxWidth={1000}
						maxLines={1}
						tooltipPlacement="top"
						useTooltip="on-truncate"
						size="paragraph-2"
						weight="semi-bold"
					/>
					<Text size="caption" weight="regular">
						{getFormattedMeetingDateString(meeting.startDateTime, meeting.endDateTime)}
					</Text>
				</div>
			</div>
			<div className="flex items-center space-x-20">
				<MeetingStatusBadge status={meeting.status} />
				<div className={isExpanded ? "rotate-180 transition duration-300" : "rotate-0 transition duration-300"}>
					<ButtonIcon
						icon={faAngleDown}
						onClick={(e) => {
							e.stopPropagation();
							toggleAccordion();
						}}
					/>
				</div>
			</div>
		</div>
	);
};

interface MeetingAccordionProps {
	meeting: MeetingModelJson;
	workspace: ExtendedWorkspace;
	userId: string;
	expandedMeetingIds: string[];
	handleToggleAccordion: (meetingId: string) => void;
	setEditingMeeting: (meeting: MeetingModelJson) => void;
	setUserAssignment: (userAssignment: {
		actionItemId: string;
		meetingId: string;
		currentAssigneeId?: string;
	}) => void;
	isAdminView?: boolean;
}

const MeetingAccordion = (props: MeetingAccordionProps) => {
	const {
		meeting,
		workspace,
		userId,
		expandedMeetingIds,
		handleToggleAccordion,
		setEditingMeeting,
		setUserAssignment,
		isAdminView = false,
	} = props;
	const isExpanded = expandedMeetingIds.includes(meeting._id);

	// For meeting participants, we need to properly identify:
	// 1. Meeting creator (first participant)
	// 2. Other participant (the one who is not the meeting creator)
	const meetingCreatorId = meeting.created?.userId;
	const otherParticipantId = meeting.participants?.find((p) => p.userId !== meetingCreatorId)?.userId;

	// In admin view, participantId should be the "other" participant (not the creator)
	// In regular view, participantId should be the participant who is not the current user
	const participantId = isAdminView
		? otherParticipantId
		: meeting.participants?.find((p) => p.userId !== userId)?.userId;

	const participantName = workspace?.participants?.find((p) => p.userId === participantId)?.name;

	const toggleAccordion = useCallback(() => handleToggleAccordion(meeting._id), [handleToggleAccordion, meeting._id]);

	// Disable interactions for archived and canceled meetings, or if in admin view
	const isInteractionDisabled = meeting.status === "archived" || meeting.status === "canceled" || isAdminView;

	const contextValue = useMemo(
		() => ({
			meeting,
			userId,
			isExpanded,
			toggleAccordion,
			setEditingMeeting,
			setUserAssignment,
			participantId,
			participantName,
			workspace,
			isInteractionDisabled,
			isAdminView,
		}),
		[
			meeting,
			userId,
			isExpanded,
			toggleAccordion,
			setEditingMeeting,
			setUserAssignment,
			participantId,
			participantName,
			workspace,
			isInteractionDisabled,
			isAdminView,
		]
	);

	return (
		<MeetingProvider value={contextValue}>
			<div
				className={`flex w-full flex-col rounded-lg border-[1px] border-solid border-neutral-3 ${
					isExpanded ? "h-[660px]" : ""
				}`}
				role="button"
				tabIndex={0}
			>
				<MeetingAccordionHeader />
				{isExpanded && <MeetingAccordionContent />}
			</div>
		</MeetingProvider>
	);
};

export default MeetingAccordion;
